import {Injectable} from '@angular/core';
import {TranslocoService} from "@jsverse/transloco";
import {isArray, isArrayOrStringMessages, isObject} from "@shared/utils";
import {CustomToasterComponent} from "@core/libs/ngx-toast/custom-toaster.component";
import {ToastrService} from "ngx-toastr";
import {IToastAlertHtmlConfig, IToastOptions} from "@shared/models/global";
import {NgxModalCustomService} from '@shared/services/global/ngx-modal-custom.service';
import {Observable} from 'rxjs';
import {NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {AlertDialogComponent, ConfirmDialogComponent, DialogAlertData, IDialogOptions} from '@shared/components/dialog';

type ITypeColor = 'danger' | 'warning' | 'info' | 'success';
type IDialogConfig = {
  message: string | any[],
  size?: 'sm' | 'md' | 'lg' | 'xl',
  color?: ITypeColor
  keyboard?: boolean,
  data?: {
    title?: string, // Title modal
    titleOriginal?: string, // Title modal không dùng translate
    disableClose?: boolean, // Ẩn nút đóng modal
    btnCloseName?: string, // Tên close khác
    btnOtherName?: string, // Tên button khác
  },
  backdrop?: boolean | 'static', // Để static nếu không muốn click ra ngoài thì đóng modal
}

@Injectable({
  providedIn: 'root'
})
export class NotifyService {

  constructor(
    private _toast: ToastrService,
    private _transLoco: TranslocoService,
    public ngbModal: NgxModalCustomService,
  ) {
    this._toast.toastrConfig.iconClasses = {
      error: 'ph ph-x-circle',
      info: 'ph ph-info',
      success: 'ph ph-check-circle',
      warning: 'ph ph-warning',
    };
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * Notify fast messages common --------------------------------------------------------------------------------------
   * -------------------------------------------------------------------------------------------------------------------
   * */
  errorSystem() {
    this.error('common.messages.systemError')
  }

  alertHtml(opts: IToastAlertHtmlConfig) {
    if (!opts.type) {
      opts.type = 'info';
    }
    switch (opts.type) {
      case 'success':
        break;
      default:
        return this.success(opts.html, opts.title ?? '')
    }
  }

  showToast(opts: IToastOptions) {
    if (!opts.message && !opts.html) {
      return;
    }

    const config = {
      tapToDismiss: opts.config?.tapToDismiss ? opts.config?.tapToDismiss : true,
      closeButton: opts.config?.closeButton ? opts.config?.closeButton : false,
      disableTimeOut: opts.config?.disableTimeOut ? opts.config?.disableTimeOut : false,
      titleClass: opts.config?.iconClass ? opts.config?.iconClass : '',
      timeOut: opts.config?.timeOut ?? 5000,
      enableHtml: true,
      positionClass: 'toast-top-right',
      toastComponent: CustomToasterComponent
    }

    let message = opts.html ? opts.html : '';
    const title = opts.title ? opts.title : '';
    if (!message && opts.message) {
      message = this._getMessages(opts.message)
    }
    this._toast.show(message, title, config, opts.type ?? 'info');
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * Notify color -----------------------------------------------------------------------------------------------------
   * -------------------------------------------------------------------------------------------------------------------
   * */
  success(messages: string | any[], title = '', html = '') {
    if (!isArrayOrStringMessages(messages)) return;

    this.showToast({message: messages, title, html, type: 'success'})
  }

  error(messages: string | any[], title = '', html = '') {
    if (!isArrayOrStringMessages(messages)) return;

    this.showToast({message: messages, title, html, type: 'error'})
  }

  warning(messages: string | any[], title = '', html = '') {
    if (!isArrayOrStringMessages(messages)) return;

    this.showToast({message: messages, title, html, type: 'warning'})
  }

  info(messages: string | any[], title = '', html = '') {
    if (!isArrayOrStringMessages(messages)) return;

    this.showToast({message: messages, title, html, type: 'info'})
  }

  private _getMessages(messages: any) {
    if (isArray(messages)) {
      return messages.map(message => this.translateMessage(message)).join('<br>');
    } else if (isObject(messages)) {
      return Object.entries(messages).map(([, err]) => this.translateMessage(err)).join('<br>');
    }

    return this.translateMessage(messages);
  }

  /** Dịch key i18n khi có; giữ nguyên thông báo văn bản do BE trả về. */
  private translateMessage(message: unknown): string {
    if (typeof message !== 'string') return String(message ?? '');
    return this._transLoco.translate(message);
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * Alert Dialog --------------------------------------------------------------------------------------------------------
   * -------------------------------------------------------------------------------------------------------------------
   * */
  dialogConfirmInfo(opts: IDialogOptions = {}, centered = false) {
    const dialogRef: NgbModalRef = this.ngbModal.openIntro(ConfirmDialogComponent, {
      size: opts.size ? opts.size : 'sm',
      centered
    });

    if (!opts.btnConfirmClass) {
      opts.btnConfirmClass = 'btn-success'
    }
    if (!opts.btnConfirmName) {
      opts.btnConfirmName = 'common.confirm'
    }
    (dialogRef.componentInstance as ConfirmDialogComponent).data = opts;
    return dialogRef.closed;
  }

  dialogConfirmDelete(options: IDialogOptions = {}) {
    //https://work.1app.vn/new-ui-setting-gui-sms.t507418.p1?businessId=124#act-52665
    const dialogRef = this.ngbModal.openIntro(ConfirmDialogComponent, {
      size: 'sm',
      centered: true,
      backdropClass: options.backdropClass ? options.backdropClass : '',
      modalDialogClass: options.modalDialogClass ? options.modalDialogClass : '',
      windowClass: options.windowClass ? options.windowClass : '',
    });
    dialogRef.componentInstance.data = options;
    return dialogRef.closed;
  }

  dialogError(config: IDialogConfig, returnAfterClosed = false): false | Observable<any> {
    config.color = 'danger';
    const dialogRef = this._callDialog(config);
    if (returnAfterClosed) {
      return dialogRef.dismissed;
    }
    return false;
  }

  dialogWarning(config: IDialogConfig, returnAfterClosed = false) {
    config.color = 'warning';
    this._callDialog(config);
    return false;
  }

  dialogWarningDismissed(config: IDialogConfig) {
    config.color = 'warning';
    const dialogRef = this._callDialog(config);
    return dialogRef.dismissed;
  }

  dialogInfo(config: IDialogConfig) {
    config.color = 'info';
    this._callDialog(config);
  }

  dialogSuccess(config: IDialogConfig) {
    config.color = 'success';
    this._callDialog(config);
  }

  private _callDialog(config: IDialogConfig) {
    const message = this._getMessages(config.message);

    let data: DialogAlertData = {
      message,
      color: config.color ? config.color : 'info'
    };
    if (config?.data) {
      data = {...data, ...config.data}
    }
    const configModal: NgbModalOptions = {
      size: 'sm',
      ...config
    };

    const dialogRef = this.ngbModal.openIntro(AlertDialogComponent, configModal);
    dialogRef.componentInstance.data = data;
    return dialogRef;
  }

  showToastError(messages: any) {
    if (isArray(messages)) {
      this.error(messages.join('<br>'))
    } else if (isObject(messages)) {
      const msg: string[] = []
      Object.keys(messages).forEach(m => {
        msg.push(`${m}: ${messages[m]}`)
      })
      this.error(msg.join('<br>'))
    } else {
      this.error(messages)
    }
  }

  showToastSuccess(messages: any) {
    if (isArray(messages)) {
      this.success(messages.join('<br>'))
    } else if (isObject(messages)) {
      const msg: string[] = []
      Object.keys(messages).forEach(m => {
        msg.push(`${m}: ${messages[m]}`)
      })
      this.success(msg.join('<br>'))
    } else {
      this.success(messages)
    }
  }
}
